import { useState, type KeyboardEvent } from "react";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CornerDownRight, CornerLeftUp, GripVertical, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
} from "@lumanity/ui/primitives";
import { cn } from "@/lib/utils";
import { OUTPUT_TYPE_LABELS } from "@/features/outputs/model";
import type { Output } from "@/features/outputs/types";
import type { OutlineMutations } from "./draftApi";
import { GROUNDING_LABELS, childrenOf, grounding, moveItem, nest, nestTarget, toItems, totalWords, unnest, type Grounding } from "./outlineModel";
import type { Section } from "./types";

const GROUNDING_STYLES: Record<Grounding, string> = {
  grounded: "bg-emerald-100 text-emerald-800",
  requires_grounding: "bg-amber-100 text-amber-800",
};

/** A one-line text input that commits on Enter or blur and cancels on Escape. */
const InlineInput = ({ initial = "", placeholder, onCommit, onCancel }: { initial?: string; placeholder: string; onCommit: (value: string) => void; onCancel: () => void }) => {
  const [value, setValue] = useState(initial);
  const commit = () => (value.trim() && value.trim() !== initial ? onCommit(value.trim()) : onCancel());
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") commit();
    if (e.key === "Escape") onCancel();
  };
  return (
    <Input
      autoFocus
      value={value}
      placeholder={placeholder}
      aria-label={placeholder}
      maxLength={200}
      className="h-7 text-sm"
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={onKeyDown}
      onBlur={commit}
    />
  );
};

interface RowProps {
  section: Section;
  sections: Section[];
  selected: boolean;
  canWrite: boolean;
  renaming: boolean;
  onSelect: () => void;
  onRename: (title: string) => void;
  onStartRename: () => void;
  onCancelRename: () => void;
  onAddSubsection?: () => void;
  onNest?: () => void;
  onUnnest?: () => void;
  onDelete: () => void;
}

const OutlineRow = ({ section, sections, selected, canWrite, renaming, onSelect, onRename, onStartRename, onCancelRename, onAddSubsection, onNest, onUnnest, onDelete }: RowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id, disabled: !canWrite || renaming });
  const isChild = section.parent_id !== null;
  const status = isChild ? null : grounding(section, sections);

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn("group flex items-center gap-1 rounded py-1 pr-1", isChild ? "pl-5" : "pl-0", selected && "bg-brand-teal/10", isDragging && "opacity-60 z-10 relative")}
    >
      {canWrite ? (
        <button type="button" className="cursor-grab p-0.5 text-muted-foreground/60 hover:text-foreground touch-none" aria-label={`Move ${section.title}`} {...attributes} {...listeners}>
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      ) : (
        <span className="w-4" />
      )}
      {renaming ? (
        <div className="flex-1">
          <InlineInput initial={section.title} placeholder="Section title" onCommit={onRename} onCancel={onCancelRename} />
        </div>
      ) : (
        <button
          type="button"
          onClick={onSelect}
          aria-current={selected ? "true" : undefined}
          className={cn("flex-1 min-w-0 truncate text-left text-sm", isChild ? "text-brand-navy/90" : "font-semibold text-brand-navy")}
          title={section.title}
        >
          {section.title}
        </button>
      )}
      {status && !renaming && (
        <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium", GROUNDING_STYLES[status])}>{GROUNDING_LABELS[status]}</span>
      )}
      {canWrite && !renaming && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="rounded p-0.5 text-muted-foreground opacity-0 hover:bg-muted group-hover:opacity-100 focus:opacity-100" aria-label={`${section.title} options`}>
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onStartRename}>
              <Pencil className="h-4 w-4 mr-2" /> Rename
            </DropdownMenuItem>
            {onAddSubsection && (
              <DropdownMenuItem onSelect={onAddSubsection}>
                <Plus className="h-4 w-4 mr-2" /> Add subsection
              </DropdownMenuItem>
            )}
            {onNest && (
              <DropdownMenuItem onSelect={onNest}>
                <CornerDownRight className="h-4 w-4 mr-2" /> Make a subsection
              </DropdownMenuItem>
            )}
            {onUnnest && (
              <DropdownMenuItem onSelect={onUnnest}>
                <CornerLeftUp className="h-4 w-4 mr-2" /> Make a main section
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onDelete} className="text-destructive focus:text-destructive">
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </li>
  );
};

interface OutlinePanelProps {
  output: Output;
  sections: Section[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  canWrite: boolean;
  mutations: OutlineMutations;
  onError: (title: string, error: unknown) => void;
}

/**
 * The left panel of the First draft screen: the output's sections with their
 * grounding badges and the body word count. Writers add, rename, drag to
 * reorder, nest one level and delete sections.
 */
const OutlinePanel = ({ output, sections, selectedId, onSelect, canWrite, mutations, onError }: OutlinePanelProps) => {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  // "root" adds a main section; a section id adds a subsection to it.
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Section | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const items = toItems(sections);
  const limit = output.guidelines.body_limit;
  const words = totalWords(sections);
  const over = limit?.unit === "words" && words > limit.value;

  const reorder = (next: ReturnType<typeof moveItem>) => {
    if (next) mutations.reorder.mutate(next, { onError: (e) => onError("Could not move the section", e) });
  };
  const onDragEnd = ({ active, over: target }: DragEndEvent) => {
    if (target) reorder(moveItem(items, String(active.id), String(target.id)));
  };
  const rename = (section: Section, title: string) => {
    setRenamingId(null);
    mutations.update.mutate({ id: section.id, patch: { version: section.version, title } }, { onError: (e) => onError("Could not rename the section", e) });
  };
  const add = (title: string, parentId: string | null) => {
    setAddingTo(null);
    mutations.add.mutate({ title, parent_id: parentId }, { onSuccess: (s) => onSelect(s.id), onError: (e) => onError("Could not add the section", e) });
  };
  const confirmDelete = () => {
    if (!deleting) return;
    mutations.remove.mutate(deleting.id, { onError: (e) => onError("Could not delete the section", e) });
    setDeleting(null);
  };

  const addInput = (parentId: string | null) => (
    <li key={`add-${parentId ?? "root"}`} className={cn("py-1", parentId ? "pl-9" : "pl-5")}>
      <InlineInput placeholder={parentId ? "Subsection title" : "Section title"} onCommit={(t) => add(t, parentId)} onCancel={() => setAddingTo(null)} />
    </li>
  );

  return (
    <aside className="rounded-lg border bg-card p-4 lg:sticky lg:top-6" aria-label="Outline">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-brand-teal">{OUTPUT_TYPE_LABELS[output.type]} outline</h2>
        <span className={cn("rounded px-1.5 py-0.5 text-xs tabular-nums", over ? "bg-red-100 text-red-800" : "bg-muted text-muted-foreground")} title="Body words">
          {words}
          {limit?.unit === "words" ? ` / ${limit.value}` : " words"}
        </span>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <ul className="space-y-0.5">
            {sections.map((section) => {
              const isLastOfBlock = section.parent_id === null ? childrenOf(sections, section.id).length === 0 : childrenOf(sections, section.parent_id).at(-1)?.id === section.id;
              const blockParent = section.parent_id ?? section.id;
              return [
                <OutlineRow
                  key={section.id}
                  section={section}
                  sections={sections}
                  selected={section.id === selectedId}
                  canWrite={canWrite}
                  renaming={renamingId === section.id}
                  onSelect={() => onSelect(section.id)}
                  onRename={(t) => rename(section, t)}
                  onStartRename={() => setRenamingId(section.id)}
                  onCancelRename={() => setRenamingId(null)}
                  onAddSubsection={section.parent_id === null ? () => setAddingTo(section.id) : undefined}
                  onNest={nestTarget(items, section.id) ? () => reorder(nest(items, section.id)) : undefined}
                  onUnnest={section.parent_id ? () => reorder(unnest(items, section.id)) : undefined}
                  onDelete={() => setDeleting(section)}
                />,
                isLastOfBlock && addingTo === blockParent ? addInput(blockParent) : null,
              ];
            })}
            {addingTo === "root" && addInput(null)}
          </ul>
        </SortableContext>
      </DndContext>

      {sections.length === 0 && <p className="py-2 text-sm text-muted-foreground">No sections yet.</p>}

      {canWrite && addingTo !== "root" && (
        <button
          type="button"
          onClick={() => setAddingTo("root")}
          className="mt-2 flex w-full items-center justify-center gap-1 rounded border border-dashed py-1.5 text-sm font-medium text-brand-navy hover:bg-muted"
        >
          <Plus className="h-4 w-4" /> Add section
        </button>
      )}

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>Its text, sources and instructions go too{deleting && childrenOf(sections, deleting.id).length > 0 ? ", along with its subsections" : ""}. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
};

export default OutlinePanel;
